import { API_URL, catalogRequest } from './axiosInstance';

/* ===========================================================================
   WHICH PRODUCTS REQUIRE A PRESCRIPTION

   This is the one thing the Store API cannot tell us, and it is the reason
   ARCH-5 could not be a straight swap. Prescription-only status lives in
   WooCommerce's `purchase_note` field, which `wc/store/v1` does not expose.
   Measured against the live catalog: 1,604 of 2,279 products (70.4%) are gated
   this way, so losing the signal silently would drop the prescription
   requirement from most of the shop.

   Rather than keep the whole catalog on the admin API for the sake of one field,
   the requirement is resolved here for *just the products in the cart*, in a
   single request, at the moment it is needed:

       GET wc/v3/products?include=<cart ids>&_fields=id,purchase_note

   `_fields` keeps the payload to two keys per product and `include` means one
   request regardless of cart size, so this is cheap even though it still goes
   through the authenticated endpoint.

   This is also MORE correct than what it replaces. Cart.jsx previously read
   `purchase_note` off the cart object in localStorage — a copy taken whenever
   the product was added. An item added before a product was marked
   prescription-only kept the stale copy and silently bypassed the gate. Asking
   the server at checkout time cannot go stale.

   IT IS STILL NOT ENFORCEMENT. This runs in the browser, so it can be skipped,
   and `POST custom/v1/orders` does not check: it accepts `prescription_id` and
   records it if present, but never verifies one exists when the order needs it.
   That is RX-1, and it has to be fixed server-side. Treat this module as the
   thing that makes the UI correct, not as the control.
   =========================================================================== */

// Matches how the store actually marks these products: the note is rich text,
// e.g. "<p>prescription</p>". A substring check on a rendered HTML string is a
// poor way to encode a compliance rule — that is RX-2 — but it is what the data
// currently holds, and changing the data is a separate, server-side job.
const PRESCRIPTION_MARKER = 'prescription';

const requiresPrescriptionNote = (note) =>
    typeof note === 'string' && note.toLowerCase().includes(PRESCRIPTION_MARKER);

/**
 * Resolve which of the given product ids require a prescription.
 *
 * @param   {Array<number|string>} productIds
 * @returns {Promise<Set<number>>} ids requiring a prescription
 * @throws  on network/API failure — callers must decide what to do, and the
 *          safe choice is to assume a prescription IS required rather than to
 *          treat a failed lookup as "no prescription needed". See
 *          `cartRequiresPrescription`.
 */
export const getPrescriptionRequiredIds = async (productIds = []) => {
    const ids = [...new Set(
        (productIds || [])
            .map((id) => parseInt(id, 10))
            .filter((id) => Number.isFinite(id) && id > 0)
    )];

    if (ids.length === 0) return new Set();

    const response = await catalogRequest(
        'GET',
        `${API_URL}/products`,
        {},
        {
            include: ids.join(','),
            _fields: 'id,purchase_note',
            // `include` caps the result set, but per_page still has to be large
            // enough to return all of them in one page.
            per_page: Math.min(Math.max(ids.length, 1), 100),
        }
    );

    const rows = Array.isArray(response?.data) ? response.data : [];
    const required = new Set();
    for (const row of rows) {
        if (requiresPrescriptionNote(row?.purchase_note)) {
            required.add(Number(row.id));
        }
    }
    return required;
};

/**
 * Does this cart need a prescription uploaded?
 *
 * Fails CLOSED: if the lookup fails we report that a prescription is required.
 * Getting this wrong in the permissive direction means dispensing
 * prescription-only medicine without one, which is a worse outcome than asking
 * a customer for a document they did not strictly need.
 *
 * @returns {Promise<{required: boolean, ids: Set<number>, degraded: boolean}>}
 *          `degraded` is true when the answer came from the failure path, so the
 *          caller can word its message accordingly.
 */
export const cartRequiresPrescription = async (cartItems = []) => {
    const ids = (cartItems || []).map((item) => item?.id).filter(Boolean);
    if (ids.length === 0) {
        return { required: false, ids: new Set(), degraded: false };
    }

    try {
        const required = await getPrescriptionRequiredIds(ids);
        return { required: required.size > 0, ids: required, degraded: false };
    } catch (error) {
        console.error('Prescription requirement lookup failed; assuming required.', error);
        return { required: true, ids: new Set(ids.map(Number)), degraded: true };
    }
};
